// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { Geometry } from './Geometry';
import { GeocodeVerifiedGeometry } from './GeocodeVerifiedGeometry';
import { StructuredAddress } from './StructuredAddress';
import { ContactPoint } from './ContactPoint';

import type { GeocodeVerifiedGeometryModelType } from './GeocodeVerifiedGeometry';
import type { GeometryModelType } from './Geometry';
import type { StructuredAddressModelType } from './StructuredAddress';
import type { ContactPointModelType } from './ContactPoint';

export type GeocodeEditLocationModelType = {
    id: string,
    code?: string,
    groupId?: ?string,
    name?: string,
    taskId?: ?(string | number),
    index: number,
    addressColumn?: string,
    address?: StructuredAddressModelType,
    geometry?: ?GeometryModelType,
    originalGeometry?: ?GeometryModelType,
    geocodeVerifiedGeometry: ?GeocodeVerifiedGeometryModelType,
    isOriginal: boolean,
    verified?: boolean,
    archive?: Array<GeometryModelType>,
    contactPoint?: ?ContactPointModelType,
    isVerifyChanged?: boolean,
    metadata?: Array<string>,
    type?: string
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class GeocodeEditLocation extends ImmutableModel {
    static fromJS(json: GeocodeEditLocationModelType): GeocodeEditLocation {
        const state: Object = Object.assign({}, json);
        state.address = state.address
            ? StructuredAddress.fromJS(state.address)
            : state.address;
        state.geometry = state.geometry
            ? Geometry.fromJS(state.geometry)
            : state.geometry;
        state.originalGeometry = state.originalGeometry
            ? Geometry.fromJS(state.originalGeometry)
            : state.originalGeometry;
        state.geocodeVerifiedGeometry = state.geocodeVerifiedGeometry
            ? GeocodeVerifiedGeometry.fromJS(state.geocodeVerifiedGeometry)
            : state.geocodeVerifiedGeometry;
        state.archive = state.archive
            ? Immutable.List(state.archive).map(item => Geometry.fromJS(item))
            : state.archive;
        state.contactPoint = state.contactPoint
            ? ContactPoint.fromJS(state.contactPoint)
            : state.contactPoint;
        state.metadata = state.metadata
            ? Immutable.List(state.metadata)
            : state.metadata;
        return new this(Immutable.Map(state));
    }

    toJS(): GeocodeEditLocationModelType {
        const js: GeocodeEditLocationModelType = {
            id: this.id,
            index: this.index,
            geocodeVerifiedGeometry: this.geocodeVerifiedGeometry
                ? this.geocodeVerifiedGeometry.toJS()
                : this.geocodeVerifiedGeometry,
            isOriginal: this.isOriginal
        };

        if (this.code != null) {
            js.code = this.code;
        }

        if (this.groupId != null) {
            js.groupId = this.groupId;
        }

        if (this.name != null) {
            js.name = this.name;
        }

        if (this.taskId != null) {
            js.taskId = this.taskId;
        }

        if (this.addressColumn != null) {
            js.addressColumn = this.addressColumn;
        }

        if (this.address != null) {
            js.address = this.address ? this.address.toJS() : this.address;
        }

        if (this.geometry != null) {
            js.geometry = this.geometry ? this.geometry.toJS() : this.geometry;
        }

        if (this.originalGeometry != null) {
            js.originalGeometry = this.originalGeometry
                ? this.originalGeometry.toJS()
                : this.originalGeometry;
        }

        if (this.verified != null) {
            js.verified = this.verified;
        }

        if (this.archive != null) {
            js.archive = this.archive
                ? this.archive.toArray().map(item => item.toJS())
                : this.archive;
        }

        if (this.contactPoint != null) {
            js.contactPoint = this.contactPoint
                ? this.contactPoint.toJS()
                : this.contactPoint;
        }

        if (this.isVerifyChanged != null) {
            js.isVerifyChanged = this.isVerifyChanged;
        }

        if (this.metadata != null) {
            js.metadata = this.metadata
                ? this.metadata.toArray()
                : this.metadata;
        }

        if (this.type != null) {
            js.type = this.type;
        }

        return js;
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): ?string {
        return this._state.get('code');
    }

    setCode(code: ?string): this {
        return this.clone(this._state.set('code', code));
    }

    get groupId(): ?string {
        return this._state.get('groupId');
    }

    setGroupId(groupId: ?string): this {
        return this.clone(this._state.set('groupId', groupId));
    }

    get name(): ?string {
        return this._state.get('name');
    }

    setName(name: ?string): this {
        return this.clone(this._state.set('name', name));
    }

    get taskId(): ?(string | number) {
        return this._state.get('taskId');
    }

    setTaskId(taskId: ?(string | number)): this {
        return this.clone(this._state.set('taskId', taskId));
    }

    get index(): number {
        return this._state.get('index');
    }

    setIndex(index: number): this {
        return this.clone(this._state.set('index', index));
    }

    get addressColumn(): ?string {
        return this._state.get('addressColumn');
    }

    setAddressColumn(addressColumn: ?string): this {
        return this.clone(this._state.set('addressColumn', addressColumn));
    }

    get address(): ?StructuredAddress {
        return this._state.get('address');
    }

    setAddress(address: ?StructuredAddress): this {
        return this.clone(this._state.set('address', address));
    }

    get geometry(): ?Geometry {
        return this._state.get('geometry');
    }

    setGeometry(geometry: ?Geometry): this {
        return this.clone(this._state.set('geometry', geometry));
    }

    get originalGeometry(): ?Geometry {
        return this._state.get('originalGeometry');
    }

    setOriginalGeometry(originalGeometry: ?Geometry): this {
        return this.clone(
            this._state.set('originalGeometry', originalGeometry)
        );
    }

    get geocodeVerifiedGeometry(): ?GeocodeVerifiedGeometry {
        return this._state.get('geocodeVerifiedGeometry');
    }

    setGeocodeVerifiedGeometry(
        geocodeVerifiedGeometry: ?GeocodeVerifiedGeometry
    ): this {
        return this.clone(
            this._state.set('geocodeVerifiedGeometry', geocodeVerifiedGeometry)
        );
    }

    get isOriginal(): boolean {
        return this._state.get('isOriginal');
    }

    setIsOriginal(isOriginal: boolean): this {
        return this.clone(this._state.set('isOriginal', isOriginal));
    }

    get verified(): ?boolean {
        return this._state.get('verified');
    }

    setVerified(verified: ?boolean): this {
        return this.clone(this._state.set('verified', verified));
    }

    get archive(): ?Immutable.List<Geometry> {
        return this._state.get('archive');
    }

    setArchive(archive: ?Immutable.List<Geometry>): this {
        return this.clone(this._state.set('archive', archive));
    }

    get contactPoint(): ?ContactPoint {
        return this._state.get('contactPoint');
    }

    setContactPoint(contactPoint: ?ContactPoint): this {
        return this.clone(this._state.set('contactPoint', contactPoint));
    }

    get isVerifyChanged(): ?boolean {
        return this._state.get('isVerifyChanged');
    }

    setIsVerifyChanged(isVerifyChanged: ?boolean): this {
        return this.clone(this._state.set('isVerifyChanged', isVerifyChanged));
    }

    get metadata(): ?Immutable.List<string> {
        return this._state.get('metadata');
    }

    setMetadata(metadata: ?Immutable.List<string>): this {
        return this.clone(this._state.set('metadata', metadata));
    }

    get type(): ?string {
        return this._state.get('type');
    }

    setType(type: ?string): this {
        return this.clone(this._state.set('type', type));
    }
}
